import {useField} from 'formik';
import _ from 'lodash';
import React, {useEffect, useState} from 'react';
import Select from 'react-select';

const DEFAULT_MENU_PROPS = {
    menuPortalTarget: document.getElementById('root'),
    menuPosition: 'fixed',
};

export const FormikSelect = ({
    autoFocus,
    controlShouldRenderValue,
    defaultValue,
    disabled,
    filterOption,
    hideSelectedOptions,
    isClearable,
    isMulti,
    isSearchable,
    menuIsOpen,
    name,
    options,
    placeholder,
    innerRef,
    onChange,
    onFocus,
    onBlur,
    // === Internal properties === ///
    components,
}) => {
    const [field, fieldMeta, fieldHelpers] = useField(name);

    // The Formik context contains the value(s) of the option(s) selected by the user.
    // We need to also keep track of which option(s) are currently selected in order to control the <AsyncSelect> component.
    const [selectedOptions, setSelectedOptions] = useState(null);

    useEffect(() => {
        if (!_.isNil(fieldMeta.value)) {
            // Whenever the value in the Formik context changes, we need to update the selected option(s) in the <Select> component.
            // This is necessary because the Formik context may be updated by external sources, such as the parent component.
            setSelectedOptions(
                _.isArray(fieldMeta.value)
                    ? _.filter(options, (option) => _.includes(fieldMeta.value, option.value))
                    : _.find(options, {value: fieldMeta.value}),
            );
        }

        if (_.isNil(fieldMeta.value)) {
            setSelectedOptions(null);
        }
    }, [fieldMeta.value, options]);

    /**
     * This function is called whenever the user selects or removes an option from the dropdown,
     * or clears the selection.
     *
     * @param {{label: string; value: any} | Array<{label: string; value: any}> | null} option
     * The option(s) selected by the user. If the user clears the selection, this will be `null`.
     *
     * @param {import("react-select").ActionMeta<any>} action
     * Provides meta data about the action that triggered the change.
     */
    const handleSelectionChanged = (option, action) => {
        // Extract the value(s) from the selected option(s) and update the Formik context.
        const value = _.isNil(option) ? '' : _.isArray(option) ? _.map(option, 'value') : option.value;
        fieldHelpers.setValue(value);
        fieldHelpers.setTouched(true, false);

        // Update the selected option(s) in the <AsyncSelect> component.
        setSelectedOptions(option);

        // Notify consumers of the change. Provide the selected option(s) instead of just the value(s).
        _.attempt(onChange, option, action);
    };

    return (
        <Select
            {...DEFAULT_MENU_PROPS}
            autoFocus={autoFocus}
            controlShouldRenderValue={controlShouldRenderValue}
            defaultValue={defaultValue}
            hideSelectedOptions={hideSelectedOptions}
            name={name}
            classNamePrefix="form-select"
            isDisabled={disabled}
            isClearable={isClearable}
            isSearchable={isSearchable}
            isMulti={isMulti}
            filterOption={filterOption}
            components={components}
            options={options}
            placeholder={placeholder}
            menuIsOpen={menuIsOpen}
            ref={innerRef}
            styles={{
                menu: (base) => ({
                    ...base,
                    zIndex: 9999,
                    position: 'absolute',
                    overflow: 'scroll',
                    marginTop: '4px',
                }),
                menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999,
                    fontWeight: '400',
                    fontSize: '14px',
                }),
            }}
            value={selectedOptions}
            onChange={handleSelectionChanged}
            onFocus={onFocus}
            onBlur={(event) => {
                // Propagate the event to the Formik field
                field.onBlur(event);
                _.attempt(onBlur, event);
            }}
        />
    );
};
